/**
 * The units a chart's axis editor works in (#1190 review MEDIUM 2): the unit the widget's first bound tag stores its
 * readings in, and the unit the org reads it in. Read from the org's tags (the cached read every tag picker shares), by
 * name, as the live readings bind them. "" for both where no bound tag has a unit, or before the tags are read.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { isLiveApi, listOrgTags } from "@/lib/api";
import { useDisplayUnits } from "@/lib/api/display-units";
import { shownFor } from "@/lib/units/display";
import { axisDatumOf, type AxisDatum } from "./axis-units";
import type { DataSource } from "./types";

export function useAxisUnits(ds: DataSource | undefined): { stored: string; shown: string } {
  const orgTags = useQuery({ queryKey: ["org-tags"], queryFn: listOrgTags, enabled: isLiveApi, staleTime: 30_000 });
  const { shown: prefs } = useDisplayUnits();
  return useMemo(() => {
    const byName: Record<string, AxisDatum> = {};
    for (const t of orgTags.data?.items ?? []) {
      if (t.name) byName[t.name.toLowerCase()] = { unit: t.unit ?? "" };
    }
    const stored = axisDatumOf(ds, byName)?.unit ?? "";
    return { stored, shown: shownFor(stored, prefs).unit };
  }, [orgTags.data, ds, prefs]);
}
